/** Stock document kinds (API route segments of docs/contracts/inventory.md §4) and their permission keys. */
export const documentKinds = [
  "opening-stock",
  "goods-issues",
  "goods-receipts",
  "adjustments",
  "transfers",
  "stock-type-changes",
  "revaluations",
] as const;

export type DocumentKind = (typeof documentKinds)[number];

const feature: Record<DocumentKind, string> = {
  "opening-stock": "opening_stock",
  "goods-issues": "goods_issue",
  "goods-receipts": "goods_receipt",
  adjustments: "inventory_adjustment",
  transfers: "transfer_warehouse",
  "stock-type-changes": "stock_type_change",
  revaluations: "revaluation",
};

/** Translation key stem of a kind (`inv:kinds.<key>.*`). */
export const kindKey: Record<DocumentKind, string> = {
  "opening-stock": "openingStock",
  "goods-issues": "goodsIssue",
  "goods-receipts": "goodsReceipt",
  adjustments: "adjustment",
  transfers: "transfer",
  "stock-type-changes": "stockTypeChange",
  revaluations: "revaluation",
};

export type DocumentAction = "view" | "create" | "update" | "delete" | "approve" | "post" | "reverse" | "print";

/** Catalog key of an action on a kind (e.g. `inv.goods_issue.post`). */
export function kindPermission(kind: DocumentKind, action: DocumentAction): string {
  return `inv.${feature[kind]}.${action}`;
}

/** Route paths of the list and object pages of each kind (typed router: literal paths). */
export const listPaths = {
  "opening-stock": "/inv/opening-stock",
  "goods-issues": "/inv/goods-issues",
  "goods-receipts": "/inv/goods-receipts",
  adjustments: "/inv/adjustments",
  transfers: "/inv/transfers",
  "stock-type-changes": "/inv/stock-type-changes",
  revaluations: "/inv/revaluations",
} as const satisfies Record<DocumentKind, string>;

export const objectPaths = {
  "opening-stock": "/inv/opening-stock/$documentId",
  "goods-issues": "/inv/goods-issues/$documentId",
  "goods-receipts": "/inv/goods-receipts/$documentId",
  adjustments: "/inv/adjustments/$documentId",
  transfers: "/inv/transfers/$documentId",
  "stock-type-changes": "/inv/stock-type-changes/$documentId",
  revaluations: "/inv/revaluations/$documentId",
} as const satisfies Record<DocumentKind, string>;

export function isDocumentKind(value: string): value is DocumentKind {
  return (documentKinds as readonly string[]).includes(value);
}

/** Line columns a kind uses. */
export interface KindShape {
  /** Lines carry a unit cost (opening stock: required; receipts and gains: optional). */
  unitCost: boolean;
  /** Header destination warehouse. */
  toWarehouse: boolean;
  /** Header target stock type. */
  toStockType: boolean;
  /** Reason on the header (issue / receipt) or per line (adjustment). */
  reason: "none" | "header" | "line";
  /** Signed quantities (adjustments). */
  signed: boolean;
  /** Revaluation: NRV unit cost or reversal amount, no quantity. */
  revaluation: boolean;
}

export const kindShape: Record<DocumentKind, KindShape> = {
  "opening-stock": {
    unitCost: true,
    toWarehouse: false,
    toStockType: false,
    reason: "none",
    signed: false,
    revaluation: false,
  },
  "goods-issues": {
    unitCost: false,
    toWarehouse: false,
    toStockType: false,
    reason: "header",
    signed: false,
    revaluation: false,
  },
  "goods-receipts": {
    unitCost: true,
    toWarehouse: false,
    toStockType: false,
    reason: "header",
    signed: false,
    revaluation: false,
  },
  adjustments: {
    unitCost: true,
    toWarehouse: false,
    toStockType: false,
    reason: "line",
    signed: true,
    revaluation: false,
  },
  transfers: {
    unitCost: false,
    toWarehouse: true,
    toStockType: false,
    reason: "none",
    signed: false,
    revaluation: false,
  },
  "stock-type-changes": {
    unitCost: false,
    toWarehouse: false,
    toStockType: true,
    reason: "none",
    signed: false,
    revaluation: false,
  },
  revaluations: {
    unitCost: true,
    toWarehouse: false,
    toStockType: false,
    reason: "none",
    signed: false,
    revaluation: true,
  },
};

/** Stock types (IV7). */
export const stockTypes = ["unrestricted", "quality", "blocked"] as const;
export type StockType = (typeof stockTypes)[number];

/** Report keys (`GET reports/{key}`) and their permission keys. */
export const reportKeys = [
  "stock-card",
  "stock-balance",
  "valuation",
  "movements",
  "batch-ageing",
  "serial-trace",
  "slow-moving",
  "reconciliation",
] as const;
export type ReportKey = (typeof reportKeys)[number];

export const reportPermission: Record<ReportKey, { view: string; print: string }> = {
  "stock-card": { view: "rpt.stock_card.view", print: "rpt.stock_card.print" },
  "stock-balance": { view: "rpt.stock_balance.view", print: "rpt.stock_balance.print" },
  valuation: { view: "rpt.ending_inventory_balance.view", print: "rpt.ending_inventory_balance.print" },
  movements: { view: "rpt.stock_movement_history.view", print: "rpt.stock_movement_history.print" },
  "batch-ageing": { view: "rpt.stock_batch_no_balance.view", print: "rpt.stock_batch_no_balance.print" },
  "serial-trace": { view: "rpt.serial_trace.view", print: "rpt.serial_trace.print" },
  "slow-moving": { view: "rpt.slow_moving_items.view", print: "rpt.slow_moving_items.print" },
  reconciliation: { view: "rpt.stock_gl_reconciliation.view", print: "rpt.stock_gl_reconciliation.print" },
};

export const reportKeyName: Record<ReportKey, string> = {
  "stock-card": "stockCard",
  "stock-balance": "stockBalance",
  valuation: "valuation",
  movements: "movements",
  "batch-ageing": "batchAgeing",
  "serial-trace": "serialTrace",
  "slow-moving": "slowMoving",
  reconciliation: "reconciliation",
};

export function isReportKey(value: string): value is ReportKey {
  return (reportKeys as readonly string[]).includes(value);
}
